import { useState, useCallback } from "react"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../firebase"
import { saveDesign as saveDesignUtil, getUserDesigns, updateDesign, deleteDesign as deleteDesignUtil } from "../utils/firebaseUtils"

/**
 * Hook to manage personal design operations
 * Handles: loading, saving, deleting, renaming, and thumbnail generation
 * @param {Object} currentUser - Current Firebase user
 * @returns {Object} Design management state and functions
 */
export function useDesignManagement(currentUser) {
  const [userDesigns, setUserDesigns] = useState([])
  const [designThumbnails, setDesignThumbnails] = useState({})
  const [editingDesignName, setEditingDesignName] = useState(null)
  const [editingDesignNameValue, setEditingDesignNameValue] = useState("")

  // Generate thumbnail for a design using Konva
  const generateThumbnail = useCallback(async design => {
    try {
      if (!window.Konva) {
        console.error("Konva not available on window object")
        return
      }

      const tempStage = new window.Konva.Stage({
        container: document.createElement("div"),
        width: 200,
        height: 280,
      })

      const tempLayer = new window.Konva.Layer()
      tempStage.add(tempLayer)

      // Handle images first (async)
      const imagePromises = design.elements
        .filter(el => el.type === "image")
        .map(el => {
          return new Promise(resolve => {
            const img = new Image()
            img.onload = () => {
              const konvaImage = new window.Konva.Image({
                x: el.x * 0.27,
                y: el.y * 0.27,
                width: el.width * 0.27,
                height: el.height * 0.27,
                image: img,
              })
              tempLayer.add(konvaImage)
              resolve()
            }
            img.onerror = () => {
              const placeholder = new window.Konva.Rect({
                x: el.x * 0.27,
                y: el.y * 0.27,
                width: el.width * 0.27,
                height: el.height * 0.27,
                fill: "#f0f0f0",
                stroke: "#ccc",
                strokeWidth: 1,
              })
              tempLayer.add(placeholder)
              resolve()
            }
            img.src = el.src
          })
        })

      await Promise.all(imagePromises)

      // Render other design elements
      design.elements.forEach(el => {
        let konvaNode = null

        if (el.type === "text") {
          konvaNode = new window.Konva.Text({
            x: el.x * 0.27,
            y: el.y * 0.27,
            text: el.text,
            fontSize: (el.fontSize || 20) * 0.27,
            fontFamily: el.fontFamily || "Arial",
            fill: el.fill || "black",
            rotation: el.rotation || 0,
          })
        } else if (el.type === "rect") {
          konvaNode = new window.Konva.Rect({
            x: el.x * 0.27,
            y: el.y * 0.27,
            width: el.width * 0.27,
            height: el.height * 0.27,
            fill: el.fill || "blue",
          })
        } else if (el.type === "circle") {
          konvaNode = new window.Konva.Circle({
            x: el.x * 0.27,
            y: el.y * 0.27,
            radius: el.radius * 0.27,
            fill: el.fill || "green",
          })
        } else if (el.type === "line") {
          konvaNode = new window.Konva.Line({
            points: el.points.map(p => p * 0.27),
            stroke: el.stroke || "red",
            strokeWidth: (el.strokeWidth || 5) * 0.27,
          })
        } else if (el.type === "image") {
          return
        }

        if (konvaNode) {
          tempLayer.add(konvaNode)
        }
      })

      tempLayer.draw()

      const thumbnailDataUrl = tempStage.toDataURL({
        mimeType: "image/png",
        quality: 0.8,
        pixelRatio: 1,
      })

      tempStage.destroy()

      setDesignThumbnails(prev => ({
        ...prev,
        [design.id]: thumbnailDataUrl,
      }))
    } catch (err) {
      console.error("Error generating thumbnail:", err)
    }
  }, [])

  // Load user's saved designs
  const loadUserDesigns = useCallback(async () => {
    if (!currentUser) return

    try {
      const designs = await getUserDesigns(currentUser.uid)
      setUserDesigns(designs)
      return designs
    } catch (err) {
      console.error("Error loading designs:", err.message)
      return []
    }
  }, [currentUser])

  // Load a specific design
  const loadDesign = useCallback(async designId => {
    try {
      const designRef = doc(db, "designs", designId)
      const designSnap = await getDoc(designRef)
      if (designSnap.exists()) {
        return designSnap.data()
      } else {
        console.error("Design not found")
        return null
      }
    } catch (err) {
      console.error("Error loading design:", err.message)
      return null
    }
  }, [])

  // Save a design
  const saveDesign = useCallback(
    async (designData, studioId = null) => {
      if (!currentUser) return false

      try {
        await saveDesignUtil(
          {
            elements: designData.elements,
            name: designData.name,
          },
          currentUser.uid,
          studioId,
        )
        return true
      } catch (err) {
        console.error("Error saving design:", err.message)
        return false
      }
    },
    [currentUser],
  )

  // Delete a design
  const deleteDesign = useCallback(
    async designId => {
      try {
        await deleteDesignUtil(designId, currentUser.uid)
        setUserDesigns(prev => prev.filter(design => design.id !== designId))
        return true
      } catch (err) {
        console.error("Error deleting design:", err.message)
        return false
      }
    },
    [currentUser],
  )

  // Rename a design
  const renameDesign = useCallback(
    async (designId, newName) => {
      if (!newName.trim()) {
        console.error("Design name cannot be empty")
        return false
      }

      try {
        await updateDesign(designId, { name: newName.trim() }, currentUser.uid)
        setUserDesigns(prev => prev.map(design => (design.id === designId ? { ...design, name: newName.trim() } : design)))
        return true
      } catch (err) {
        console.error("Error renaming design:", err.message)
        return false
      }
    },
    [currentUser],
  )

  // Start editing design name
  const startEditingDesignName = useCallback(design => {
    setEditingDesignName(design.id)
    setEditingDesignNameValue(
      design.name || `Design ${design.id.split("_")[1] ? new Date(parseInt(design.id.split("_")[1])).toLocaleDateString() : "Unknown"}`,
    )
  }, [])

  // Finish editing design name
  const finishEditingDesignName = useCallback(async () => {
    if (editingDesignName && editingDesignNameValue.trim()) {
      await renameDesign(editingDesignName, editingDesignNameValue)
    }
    setEditingDesignName(null)
    setEditingDesignNameValue("")
  }, [editingDesignName, editingDesignNameValue, renameDesign])

  // Generate thumbnails for all designs
  const generateAllThumbnails = useCallback(async () => {
    for (const design of userDesigns) {
      if (!designThumbnails[design.id]) {
        await generateThumbnail(design)
      }
    }
  }, [userDesigns, designThumbnails, generateThumbnail])

  return {
    userDesigns,
    setUserDesigns,
    designThumbnails,
    setDesignThumbnails,
    editingDesignName,
    setEditingDesignName,
    editingDesignNameValue,
    setEditingDesignNameValue,
    loadUserDesigns,
    loadDesign,
    saveDesign,
    deleteDesign,
    renameDesign,
    startEditingDesignName,
    finishEditingDesignName,
    generateThumbnail,
    generateAllThumbnails,
  }
}
